import { Close } from '@mui/icons-material'
import {
  Dialog,
  DialogTitle,
  IconButton,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import { PropsWithChildren, ReactNode } from 'react'

import rem from '~/utils/ui'

type TProps = PropsWithChildren<{
  isOpen: boolean
  onClose: () => void
  sx?: SxProps<Theme>
  subtitle?: ReactNode
  title: string
}>

type TDialogWrapperSubtitleProps = {
  subtitle?: ReactNode
}

function DialogWrapperSubtitle({
  subtitle = undefined,
}: TDialogWrapperSubtitleProps) {
  const theme = useTheme()

  if (!subtitle) {
    return null
  }

  if (typeof subtitle === 'string') {
    return (
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          marginTop: rem(8),
        }}
      >
        {subtitle}
      </Typography>
    )
  }

  return subtitle
}

function DialogWrapper({
  children = null,
  isOpen,
  onClose,
  sx = {},
  subtitle = undefined,
  title,
}: TProps) {
  const theme = useTheme()

  const handleDialogClose = () => {
    onClose()
  }

  return (
    <Dialog
      onClose={handleDialogClose}
      open={isOpen}
      sx={{
        '.MuiDialog-paper.MuiPaper-root': {
          borderRadius: rem(6),
          margin: 0,
          overflow: 'hidden',
          padding: rem(24),
          position: 'relative',
          maxWidth: rem(560),
          width: '100%',
          [theme.breakpoints.down('sm')]: {
            width: '100%',
            height: '100%',
            maxHeight: '100%',
          },
        },
        ...sx,
      }}
    >
      <DialogTitle
        sx={{
          padding: 0,
          fontSize: rem(18),
          fontWeight: 600,
          lineHeight: rem(18),
        }}
      >
        {title}
      </DialogTitle>
      <DialogWrapperSubtitle subtitle={subtitle} />
      <IconButton
        disableRipple
        onClick={handleDialogClose}
        sx={{
          color: theme.palette.tertiary1,
          width: rem(16),
          height: rem(16),
          padding: 0,
          marginLeft: 'auto',
          position: 'absolute',
          transform: 'translate(50%, -50%)',
          top: rem(24),
          right: rem(24),
        }}
      >
        <Close sx={{ fontSize: rem(16) }} />
      </IconButton>

      {children}
    </Dialog>
  )
}

export default DialogWrapper
